import { getNextPhase } from "../phases/phase-machine";
import { resolveCleanupPhase } from "../phases/cleanup-phase";
import { resolveDrawPhase } from "../phases/resolve-draw-phase";
import { resolveSupplyEntry } from "../phases/supply-phase";
import { LANE_IDS, type GameEvent, type GameState, type LaneState, type PlayerId } from "../types";
import { getOpponentPlayerId } from "../types";
import { armNoTerritoryDeadline, resolveNoTerritoryDeadline } from "./territory";

export interface TransitionResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

function resolveReadyPhase(state: Readonly<GameState>, actionSequence: number): TransitionResult {
  const playerId = state.activePlayerId;
  const events: GameEvent[] = [];
  const lanes = { ...state.lanes } as Record<keyof typeof state.lanes, LaneState>;

  for (const laneId of LANE_IDS) {
    const lane = state.lanes[laneId];
    const side = lane.players[playerId];
    const commander = side.commander;

    if (lane.result !== "active" || side.status !== "active" || commander === null) {
      continue;
    }

    lanes[laneId] = {
      ...lane,
      players: {
        ...lane.players,
        [playerId]: {
          ...side,
          commander: {
            ...commander,
            readiness: "ready",
            deployedThisTurn: false,
          },
        },
      },
    };

    events.push({
      type: "commander-readied",
      eventId: eventId(actionSequence, events.length),
      actionSequence,
      playerId,
      laneId,
      commanderCardInstanceId: commander.cardInstanceId,
    });
  }

  const nextPhase = "draw";
  events.push({
    type: "phase-changed",
    eventId: eventId(actionSequence, events.length),
    actionSequence,
    from: "ready",
    to: nextPhase,
    previousActivePlayerId: playerId,
    activePlayerId: playerId,
    turnNumber: state.turnNumber,
  });

  const enteredDrawState: GameState = { ...state, lanes, phase: nextPhase };
  if (
    enteredDrawState.schemaVersion === 2 ||
    enteredDrawState.schemaVersion === 3 ||
    enteredDrawState.schemaVersion === 4
  ) {
    const drawResolution = resolveDrawPhase(enteredDrawState, actionSequence, events.length);
    return {
      state: drawResolution.state,
      events: [...events, ...drawResolution.events],
    };
  }

  return { state: enteredDrawState, events };
}

function passTurn(
  state: Readonly<GameState>,
  actionSequence: number,
  startingEventOrdinal: number,
): TransitionResult {
  const previousActivePlayerId = state.activePlayerId;
  const activePlayerId: PlayerId = getOpponentPlayerId(previousActivePlayerId);
  const turnNumber = state.turnNumber + 1;

  const nextState: GameState = {
    ...state,
    activePlayerId,
    phase: "ready",
    turnNumber,
  };

  return {
    state: nextState,
    events: [
      {
        type: "phase-changed",
        eventId: eventId(actionSequence, startingEventOrdinal),
        actionSequence,
        from: "cleanup",
        to: "ready",
        previousActivePlayerId,
        activePlayerId,
        turnNumber,
      },
      {
        type: "cleanup-completed",
        eventId: eventId(actionSequence, startingEventOrdinal + 1),
        actionSequence,
        playerId: previousActivePlayerId,
        nextActivePlayerId: activePlayerId,
        nextTurnNumber: turnNumber,
      },
    ],
  };
}

export function applyAdvancePhase(
  state: Readonly<GameState>,
  actionSequence: number,
): TransitionResult {
  if (state.phase === "ready") {
    return resolveReadyPhase(state, actionSequence);
  }

  if (state.phase === "cleanup") {
    const cleanup = resolveCleanupPhase(state, actionSequence, 0);
    const handoff = passTurn(cleanup.state, actionSequence, cleanup.events.length);
    return { state: handoff.state, events: [...cleanup.events, ...handoff.events] };
  }

  if (state.phase === "land") {
    const deadline = resolveNoTerritoryDeadline(state, actionSequence, 0);
    if (deadline !== null) return deadline;
  }

  const nextPhase = getNextPhase(state.phase);
  if (nextPhase === null) {
    throw new Error("Cannot advance from the finished phase.");
  }

  const phaseEvent: GameEvent = {
    type: "phase-changed",
    eventId: eventId(actionSequence, 0),
    actionSequence,
    from: state.phase,
    to: nextPhase,
    previousActivePlayerId: state.activePlayerId,
    activePlayerId: state.activePlayerId,
    turnNumber: state.turnNumber,
  };
  const enteredState: GameState = armNoTerritoryDeadline({ ...state, phase: nextPhase });
  if (
    nextPhase === "supply" &&
    (enteredState.schemaVersion === 3 || enteredState.schemaVersion === 4)
  ) {
    const supply = resolveSupplyEntry(enteredState, actionSequence, 1);
    return { state: supply.state, events: [phaseEvent, ...supply.events] };
  }

  if (
    state.phase === "supply" &&
    (enteredState.schemaVersion === 3 || enteredState.schemaVersion === 4)
  ) {
    return {
      state: { ...enteredState, supplyStatus: { type: "not-started" } },
      events: [phaseEvent],
    };
  }

  return { state: enteredState, events: [phaseEvent] };
}
